import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "../components/Button";
import Navbar from "../components/layout/Navbar";
import { Footer } from "../components/layout/Footer";
import {
  AlignCenterVertical,
  Award,
  ChevronLeft,
  ChevronRight,
  CreditCard,
  FileText,
  FlaskConical,
  Info,
  LibraryBig,
  Minus,
  MoreHorizontal,
  Plus,
  Smartphone,
  TrendingUp,
} from "lucide-react";
import { MouseEvent, useEffect, useState, useMemo } from "react";
import { DEFAULTS, PRICING } from "../utils/pricing";
import { useDispatch, useSelector } from "react-redux";
import { selectGlobalData } from "../features/appSlice";
import { trackEvent } from "../utils/posthog";
import {
  logout,
  selectCurrentUser,
  selectRefreshToken,
  selectToken,
} from "../features/authSlice";
import supabase from "../helper/supabaseClient";
import { config } from "../utils/config";
import { redirectToDashboard } from "../utils/dashboardRedirect";

const items = [
  {
    title: "Create A Store in Minutes",
    description:
      "Stop losing customers due to WhatsApp and Instagram limitations. Our guided setup asks simple questions about your business and automatically creates your perfect store - whether you're selling fashion, beauty products, food, or digital services.",
  },
  {
    title: "Business Protection",
    description:
      "Sleep easy with enterprise-grade security, automatic backups, and fraud prevention. We handle the technical safeguards so you can focus on growing your business risk-free.",
  },
  {
    title: "Mobile-First Design",
    description:
      "Over 80% of your customers shop on phones. Our stores look stunning on any device, with lightning-fast loading times that keep mobile shoppers engaged and buying.",
  },
  {
    title: "Never Miss a Sale Again",
    description:
      "24/7 automated storefront means your business never sleeps. Capture orders while you're offline with integrated payments, cart recovery, and instant order notifications.",
  },
  {
    title: "See Who's Looking",
    description:
      "Get real-time insights about your visitors - see what products they view, how long they stay, and what makes them buy. Turn anonymous traffic into known customers.",
  },
];

const featureItems = [
  {
    icon: <AlignCenterVertical className="rotate-180" color="#7E57C2" />,
    title: "Empathy-Driven Approach",
    description:
      "We design with empathy. We understand our users' struggles, goals, and needs, and create solutions that cater to their unique challenges and aspirations.",
  },
  {
    icon: <Award color="#7E57C2" />,
    title: "Earn customer trust",
    description:
      "Our websites come with integrated payment services like Paystack which is secure and highly trusted by customers globally. Instantly look more legit!",
  },
  {
    icon: <LibraryBig color="#7E57C2" />,
    title: "Keep Customers updated",
    description:
      "Our websites allow you to upload and showcase all your products, so your customers will be informed on what products are available at a glance.",
  },
  {
    icon: <CreditCard color="#7E57C2" />,
    title: "Make sales in your sleep",
    description:
      "With fully automated payment systems, customers are able to make purchases from your website even while you're offline.",
  },
];

const slides = [
  {
    title: "Store Essentials",
    items: [
      "Beautiful responsive templates that look great on all devices",
      "Unlimited product listings with photos and videos",
      "Custom domain integration (.com, .com.ng, or free .jellosite.store)",
      "Dynamic delivery pricing options for physical products",
      "99.9% uptime for reliable 24/7 sales",
    ],
  },
  {
    title: "Marketing & Analytics",
    items: [
      "Facebook Pixel integration for ad campaign tracking",
      "Built-in visitor statistics to monitor store traffic",
      "Product performance tracking to optimize your offerings",
      "SEO-friendly pages to help customers find you online",
    ],
  },
  {
    title: "Payment Solutions",
    items: [
      "Seamless Paystack integration",
      "Flutterwave payment processing",
      "Automated payment confirmation",
      "Secure checkout process that builds customer trust",
    ],
  },
  {
    title: "Communication Tools",
    items: [
      "WhatsApp integration (Coming soon!)",
      "Automatic order notifications",
      "Customer account creation",
      "Product wishlist and favorites",
    ],
  },
  {
    title: "Smart Business Tools",
    items: [
      "Guided store setup based on your business type",
      "Helpful tooltips throughout the dashboard",
      "Mobile-friendly admin interface to manage on the go",
      "Simple inventory management",
    ],
  },
];

const storeNameSuggestions = [
  "My Fashion Store",
  "Lagos Sneakers",
  "Tech Gadgets Hub",
  "Mama's Kitchen",
];

const LandingPage = () => {
  const dispatch = useDispatch();
  const [activeIndex, setActiveIndex] = useState(0);
  const [slideIndex, setSlideIndex] = useState(0);
  const [storeName, setStoreName] = useState('')
  const [placeholder, setPlaceholder] = useState("");
  const [hasValidSession, setHasValidSession] = useState(false);
  const token = useSelector(selectToken);
  const refreshToken = useSelector(selectRefreshToken);
  const currentUser = useSelector(selectCurrentUser);
  const { userLocation } = useSelector(selectGlobalData);
  
  const prices = useMemo(() => {
      const country = userLocation?.country;
      if (country && PRICING[country]) {
        return PRICING[country];
      }
      return DEFAULTS;
    }, [userLocation]);

  const iconVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.3,
        ease: "easeOut",
      },
    },
  };

  const contentVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut",
      },
    },
  };

  const listContainerVariants = {
    hidden: { opacity: 1 },
    visible: {
      transition: {
        staggerChildren: 0.2,
        when: "beforeChildren",
      },
    },
  };

  const listItemVariants = {
    hidden: { opacity: 1 },
    visible: { opacity: 1 },
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        when: "beforeChildren",
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 },
    },
  };

  const slideVariants = {
    hidden: { opacity: 0, x: 100 },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.09,
        ease: "easeOut",
      },
    },
  };

  const imageVariants = {
    hidden: { x: -200, opacity: 0 },
    visible: {
      x: 0,
      opacity: 1,
      transition: {
        type: "spring",
        damping: 10,
        bounce: 0.5,
      },
    },
  };

  const listItemVariant = {
    hidden: (i: number) => ({
      x: 100 + i * 50, // Staggered starting position
      opacity: 0,
    }),
    visible: (i: number) => ({
      x: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 10,
        bounce: 0.3,
        delay: i * 0.1,
      },
    }),
  };

  const handleDashboardClick = async (
    event: MouseEvent<HTMLAnchorElement>
  ) => {
    event.preventDefault();
    await redirectToDashboard({
      fallbackAccessToken: token,
      fallbackRefreshToken: refreshToken,
    });
  };

  useEffect(() => {
    let wordIndex = 0;
    let charIndex = 0;
    let isDeleting = false;
    let typeTimer: ReturnType<typeof setTimeout> | null = null;
    let blinkTimer: ReturnType<typeof setInterval> | null = null;

    const type = () => {
      const currentWord =
        storeNameSuggestions[wordIndex % storeNameSuggestions.length];
      const currentText = currentWord.substring(0, charIndex);
      setPlaceholder(`${currentText}|`);

      if (!isDeleting && charIndex === currentWord.length) {
        let blinkCount = 0;
        blinkTimer = setInterval(() => {
          setPlaceholder((prev) =>
            prev.endsWith("|") ? currentText : `${currentText}|`
          );
          blinkCount++;

          if (blinkCount >= 6 && blinkTimer) {
            clearInterval(blinkTimer);
            blinkTimer = null;
            isDeleting = true;
            typeTimer = setTimeout(type, 300);
          }
        }, 500);
        return;
      }

      if (isDeleting && charIndex === 0) {
        isDeleting = false;
        wordIndex++;
        typeTimer = setTimeout(type, 500);
        return;
      }

      charIndex += isDeleting ? -1 : 1;
      const speed = isDeleting ? 50 : 100 + Math.random() * 50;
      typeTimer = setTimeout(type, speed);
    };

    typeTimer = setTimeout(type, 1000);

    return () => {
      if (typeTimer) clearTimeout(typeTimer);
      if (blinkTimer) clearInterval(blinkTimer);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const validateSession = async () => {
      if (!token || !currentUser) {
        setHasValidSession(false);
        return;
      }

      const { data: sessionData } = await supabase.auth.getSession();
      if (cancelled) return;

      const candidateToken = sessionData.session?.access_token || token;
      const { data: userData, error } = await supabase.auth.getUser(
        candidateToken
      );
      if (cancelled) return;

      if (error || !userData.user) {
        dispatch(logout());
        setHasValidSession(false);
        return;
      }

      setHasValidSession(true);
    };

    validateSession();

    return () => {
      cancelled = true;
    };
  }, [currentUser, dispatch, token]);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % items.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
  const interval = setInterval(() => {
    handleNext();
  }, 6000);
  return () => clearInterval(interval);
}, [slideIndex]);


  useEffect(() => {
    if (!location.hash) return;
    const targetId = location.hash.slice(1);
    const target = document.getElementById(targetId);
    if (target) {
      requestAnimationFrame(() => {
        target.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    }
  }, [location]);

  const handleNext = () => {
    setSlideIndex((prev) => (prev === slides.length - 1 ? 0 : prev + 1));
  };

  const handlePrev = () => {
    setSlideIndex((prev) => (prev === 0 ? slides.length - 1 : prev - 1));
  };

  const segments = [
    { color: "#1F2937", value: 100, label: "Nike Shoes", percentage: 40, offset: 0 },         // Gray-900
    { color: "#818CF8", value: 80, label: "Timberland Boots", percentage: 32, offset: -100 }, // Indigo-400
    { color: "#86EFAC", value: 50, label: "Adidas Sambas", percentage: 20, offset: -180 },    // Green-300
    { color: "#BFDBFE", value: 20, label: "Other", percentage: 8, offset: -230 }              // Blue-200
  ];

  const swipeConfidenceThreshold = 10000;
const swipePower = (offset: number, velocity: number) => {
  return Math.abs(offset) * velocity;
};
  return (
    <motion.div>
      <Navbar />
      <motion.div
        initial="hidden"
        animate="visible"
        variants={containerVariants}
        className="landing-bg pb-10 bg-cover bg-no-repeat bg-[center_-1000px] overflow-hidden sm:bg-[center_-750px]"
      >
        <div className=" ">
          <motion.div className="flex overflow-hidden bg-[#F6E4FD] w-full px-4 md:px-8 flex-col gap-7 items-center justify-center text-center pt-12 pb-0 relative z-0">
            <motion.div
              variants={itemVariants}
              className="flex flex-col items-center gap-3 "
            >
              <div className="w-fit p-[1px] rounded-lg  bg-[linear-gradient(91.51deg,#C3A7FA_-5.07%,rgba(217,197,255,0.5)_84.72%)]">
                <div className="group flex items-center gap-4 bg-[#F1F1F1] rounded-lg  pr-5 h-full w-full">
                  {/* Avatar Stack */}
                  <div className="flex -space-x-3">
                    <img
                      className="w-6 h-6 sm:w-8 sm:h-8 rounded-full border-2 border-white object-cover"
                      src="/assets/entp1.png"
                      alt="User 1"
                    />
                    <img
                      className="w-6 h-6 sm:w-8 sm:h-8 rounded-full border-2 border-white object-cover"
                      src="/assets/entp2.png"
                      alt="User 2"
                    />
                    <img
                      className="w-6 h-6 sm:w-8 sm:h-8 rounded-full border-2 border-white object-cover"
                      src="/assets/entp3.png"
                      alt="User 3"
                    />
                  </div>
                  <div className="">
                    <span className="text-gray-600 leading-[170%]">
                      Join 500+ Entrepreneurs to use Jellosite
                    </span>
                  </div>
                </div>
              </div>
              <motion.h1 className=" responsive-font-h1 font-medium leading-9 md:leading-13">
                <h1 className=" responsive-font-h1 font-semibold bg-[linear-gradient(259.49deg,#7E57C2_32.72%,#9F65A2_56.05%)] bg-clip-text text-transparent">
                  Create your online store.
                </h1>
                Make more sales. Scale your business.{" "}
              </motion.h1>
              <span className="responsive-font-p text-[#907bb4]">
                <span className=" responsive-font-p font-semibold text-[#9938cc]">
                  JELLOSITE
                </span>{" "}
                helps business owners take their business online, reach more{" "}
                <br className="hidden md:block" /> customers and make more
                sales. Create a{" "}
                <span className=" responsive-font-p font-semibold text-[#9938cc]">
                  FREE{" "}
                </span>
                store now.{" "}
              </span>
            </motion.div>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1.5, ease: "easeInOut" }}
              className="w-full max-w-sm md:max-w-fit"
            >
              <form
                action=""
                className="md:bg-white w-full flex flex-col md:flex-row border gap-5 border-gray-200 rounded-xl"
              >
                <input
                  type="text"
                  value={storeName}
                  placeholder={placeholder}
                  onChange={(e) => setStoreName(e.target.value)}
                  className=" bg-white rounded-xl  py-2 text-gray-800 text-center border-0 outline-0 md:w-82 "
                />
                <Link
                  to={`signup?name=${storeName}`}
                  onClick={() => trackEvent("Create your store (landing)")}
                >
                  <Button
                    text="Create your store"
                    className=" bg-[#945bf5] mx-auto rounded-[40px] w-fit transition-all duration-300 ease-in-out hover:bg-white hover:text-[#945bf5]  hover:shadow-[0_0_0_2px_#945bf5]"
                  />
                </Link>
              </form>
            </motion.div>
            <motion.ul
              variants={containerVariants}
              className="flex flex-col md:flex-row gap-2 md:gap-0 items-center justify-center list-disc md:list-none"
            >
              <li className="text-[#5B5B5B] responsive-font-li">
                No Technical Knowledge Needed
              </li>
              <hr className=" hidden md:block rotate-90 w-6 h-1 text-[#7E57C2]" />
              <li className="text-[#5B5B5B] responsive-font-li">
                Works For All Businesses
              </li>
              <hr className=" hidden md:block rotate-90 w-6 h-1 text-[#7E57C2]" />
              <li className="text-[#5B5B5B] responsive-font-li">Quick Setup</li>
            </motion.ul>
            <motion.div
              whileHover={{ scale: 1.03 }}
              transition={{ duration: 0.4, ease: "easeOut" }}
              className="relative -mb-20 md:-mb-32 lg:-mb-44 z-50 w-full max-w-5xl mx-auto"
            >
              {/* First Floter */}
              <div className=" absolute top-[9%] -left-[2%] md:-left-[5%] w-[30%] z-10">
                <motion.img
                  src="/assets/floater1.svg"
                  alt="Notification"
                  className="w-full h-auto"
                  variants={imageVariants}
                />
              </div>
              {/* Second Floater */}
              <div className="absolute -top-[7%] left-[32%] w-[30%] z-10">
                <motion.img
                  src="/assets/floater2.svg"
                  alt="Notification"
                  className="w-full h-auto"
                  variants={imageVariants}
                />
              </div>

              {/* Third Floater */}
              <div className="absolute bottom-[33%] -right-[3%] xl:-right-[5%] w-[45%]  z-10">
                <motion.img
                  src="/assets/floater3.svg"
                  alt="Notification"
                  className="w-full h-auto"
                  variants={imageVariants}
                />
              </div>

              {/*  */}

              <motion.img
                src="/assets/Desktop.png"
                alt=""
                className="w-full h-auto"
                variants={itemVariants}
              />
            </motion.div>
          </motion.div>

          <div className="flex items-center flex-col justify-center gap-10 md:my-20 my-15 px-1 md:px-10">
            <h2 className="font-bold text-center">Built for Entrepreneurs</h2>
            <div className="flex items-center md:items-start flex-col gap-20 md:gap-20 md:flex-row justify-center h-[700px] md:h-fit">
              {/* Animated Image */}
              <motion.div
                className="md:sticky md:top-20 h-fit"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "0px 0px -100px 0px" }}
                variants={imageVariants}
              >
                <motion.img
                  src="/assets/Left@2x.png"
                  alt=""
                  className="w-[300px] sm:w-[600px] md:min-h-[400px] object-contain transition-opacity duration-300 hover:opacity-95"
                  whileHover={{ scale: 1.05 }}
                />
              </motion.div>

              {/* Animated List */}
              <motion.ul
                className="space-y-2 flex items-center justify-start relative md:top-15 flex-col w-full max-h-[400px] md:max-w-[600px]"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "0px 0px -100px 0px" }}
              >
                {items.map((item, index) => (
                  <motion.li
                    key={index}
                    custom={index}
                    variants={listItemVariant}
                    className="group relative transition-all duration-300 cursor-pointer"
                    style={{ minHeight: "3rem" }}
                    onMouseEnter={() => setActiveIndex(index)}
                    onClick={() => setActiveIndex(index)}
                  >
                    <div className="flex items-start">
                      <hr
                        className={`h-full w-[2px] border-0 bg-[#CBD5E1] group-hover:bg-[#7E57C2] absolute left-2 -translate-x-1/2 transition-all duration-300 origin-bottom opacity-100 
                ${activeIndex === index ? "!bg-[#7E57C2]" : ""}`}
                      />
                      <div className="pl-8 w-full">
                        <div
                          className={`group-hover:bg-[#F9F5FF] p-1 rounded-lg 
                  ${activeIndex === index ? "!bg-[#F9F5FF]" : ""}`}
                        >
                          <h4 className="font-semibold text-gray-900 transition-colors duration-300">
                            {item.title}
                          </h4>
                          <div
                            className={`overflow-hidden transition-all duration-300 
                    ${activeIndex === index ? "max-h-[300px]" : "max-h-0"} 
                    group-hover:max-h-[300px]`}
                          >
                            <p
                              className={`pt-2 text-slate-500 opacity-0 transition-opacity duration-300 
                      group-hover:opacity-100 
                      ${activeIndex === index ? "!opacity-100" : ""}`}
                            >
                              {item.description}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.li>
                ))}
              </motion.ul>
            </div>
          </div>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={containerVariants}
            className="relative w-full overflow-hidden"
          >
            <motion.div className="absolute inset-0 z-0">
              <motion.img
                variants={imageVariants}
                src="/assets/Dmimg.png"
                className="w-full h-full object-cover"
                alt="Background"
              />
            </motion.div>

            <div className="absolute inset-0 bg-[#0F0520]/80 z-10" />

            <div className="relative z-20 flex flex-col gap-8 justify-center items-center text-center px-4 py-24 md:py-32 max-w-4xl mx-auto">
              <h2 className="text-3xl md:text-5xl font-semibold text-white leading-tight md:leading-[1.2]">
                Your competitors are selling <br className="hidden md:block" />
                while you’re still replying to DMs.
              </h2>

              <p className="text-gray-200  font-light">
                For less than the cost of your data plan, you can create a
                website that runs your business 24/7.
              </p>

              {hasValidSession ? (
                <a href={config.DASHBOARD_URL} onClick={handleDashboardClick}>
                  <Button
                    text="Go to dashboard"
                    className="rounded-lg bg-[#945bf5] hover:bg-[#7e4ccb] transition-colors text-white font-bold shadow-lg"
                  />
                </a>
              ) : (
                <Link to="signup">
                  <Button
                    text="Start For Free"
                    className="rounded-lg bg-[#945bf5] hover:bg-[#7e4ccb] transition-colors text-white font-bold shadow-lg"
                  />
                </Link>
              )}
            </div>
          </motion.div>
          <motion.div
            initial="hidden"
            animate="visible"
            variants={containerVariants}
            className="flex flex-col gap-16 items-center"
          >
            <motion.h2
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "0px 0px -100px 0px" }}
              variants={slideVariants}
              className=" responsive-font-h2 text-center leading-11 font-bold mt-12 sm:mt-20 -mb-15 sm:-mb-10"
            >
              Why Choose JELLOSITE?
            </motion.h2>
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "0px 0px -100px 0px" }}
              variants={listContainerVariants}
            >
              <motion.ul
                variants={listContainerVariants}
                className="flex items-start justify-center w-full gap-10 sm:gap-20 flex-wrap py-8 border-t border-b border-t-[#E2E8F0] border-b-[#E2E8F0]"
              >
                {featureItems.map((item, index) => (
                  <motion.li
                    key={index}
                    variants={listItemVariants}
                    className="w-[270px] flex flex-col items-center justify-center text-center gap-4"
                  >
                    <motion.div
                      variants={iconVariants}
                      className="h-13 w-13 bg-[#EDE9FE] rounded-full flex items-center justify-center"
                    >
                      {item.icon}
                    </motion.div>
                    <motion.div
                      variants={contentVariants}
                      className="space-y-4"
                    >
                      <h4 className="font-bold text-gray-900">{item.title}</h4>
                      <span className="text-[#626262]">{item.description}</span>
                    </motion.div>
                  </motion.li>
                ))}
              </motion.ul>
            </motion.div>
          </motion.div>

          <div className="my-25 flex items-center justify-center flex-col gap-10 px-4">
            <motion.h2
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "0px 0px -100px 0px" }}
              variants={itemVariants}
              className=" font-bold text-center text-gray-900 leading-none"
            >
              Here’s how JELLOSITE creates a store for{" "}
              <br className="hidden sm:block" /> YOU in 3 easy steps{" "}
            </motion.h2>
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "0px 0px -100px 0px" }}
              className="flex items-center flex-col-reverse md:flex-row justify-center gap-8"
            >
              <ul className="flex items-start justify-center gap-10 flex-col">
                <li className=" flex flex-col sm:flex-row items-center sm:items-start justify-center gap-4 sm:gap-6 w-full">
                  <motion.div
                    variants={itemVariants}
                    className="w-12 h-12 rounded-full bg-[#008EFF] flex justify-center items-center text-white aspect-square shrink-0"
                  >
                    <FileText />
                  </motion.div>
                  <div className="flex items-start justify-center text-center sm:text-left flex-col gap-1 w-full">
                    <motion.h4
                      variants={itemVariants}
                      className="font-bold text-gray-900 w-full sm:w-fit"
                    >
                      Create an Account
                    </motion.h4>
                    <motion.span
                      variants={itemVariants}
                      className="text-[#12141D]  w-full sm:w-fit"
                    >
                      Sign up in minutes and access your dashboard instantly.
                    </motion.span>
                  </div>
                </li>
                <li className="flex flex-col sm:flex-row items-center sm:items-start justify-center gap-4 sm:gap-6 w-full">
                  <motion.div
                    variants={itemVariants}
                    className="w-12 h-12 rounded-full bg-[#FABB18] flex justify-center items-center text-white aspect-square shrink-0"
                  >
                    <Smartphone />
                  </motion.div>
                  <div className="flex items-start justify-center text-center sm:text-left flex-col gap-1 w-full">
                    <motion.h4
                      variants={itemVariants}
                      className="font-bold text-gray-900 w-full sm:w-fit"
                    >
                      Add Your Store
                    </motion.h4>
                    <motion.span
                      variants={itemVariants}
                      className="text-[#12141D] w-full sm:w-fit"
                    >
                      Enter your store details and we set everything up for you.
                    </motion.span>
                  </div>
                </li>
                <li className=" flex flex-col sm:flex-row items-center sm:items-start justify-center gap-4 sm:gap-6 w-full">
                  <motion.div
                    variants={itemVariants}
                    className="w-12 h-12 rounded-full bg-[#2F872D] flex justify-center items-center text-white aspect-square shrink-0"
                  >
                    <FlaskConical />
                  </motion.div>
                  <div className="flex items-start justify-center text-center sm:text-left flex-col gap-1 w-full ">
                    <motion.h4
                      variants={itemVariants}
                      className="font-bold  text-gray-900 w-full sm:w-fit"
                    >
                      Start Selling
                    </motion.h4>
                    <motion.span
                      variants={itemVariants}
                      className="text-[#12141D]  w-full sm:w-fit"
                    >
                      Publish your store and begin receiving orders right away.
                    </motion.span>
                  </div>
                </li>
              </ul>
              <motion.img
                variants={imageVariants}
                src="/assets/PhoneGroup.svg"
                className="w-full max-w-[300px] sm:max-w-[450px]"
                alt=""
              />
            </motion.div>
          </div>
        </div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "0px 0px -100px 0px" }}
          variants={containerVariants}
          className="py-10 bg-[#FEFFF1]"
        >
          <motion.h2
            variants={itemVariants}
            className="text-center font-bold leading-9 px-3"
          >
            Powerful Features in Every Jellosite <br className="hidden sm:block" /> Store
          </motion.h2>

        <div className="relative py-12 flex flex-col items-center justify-center overflow-hidden min-h-[600px] md:min-h-[500px]">
          <div className="relative w-full max-w-5xl h-[450px] md:h-[350px] flex items-center justify-center">
            
            <AnimatePresence initial={false}>
              <motion.div
                key={slideIndex}
                drag="x"
                dragConstraints={{ left: 0, right: 0 }}
                dragElastic={1}
                onDragEnd={(e, { offset, velocity }) => {
                      const swipe = swipePower(offset.x, velocity.x);
                      if (swipe < -swipeConfidenceThreshold) {
                        handleNext();
                      } else if (swipe > swipeConfidenceThreshold) {
                        handlePrev();
                      }
                    }}
                initial={{ opacity: 0, x: 100 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -100 }}
                transition={{
                  x: { type: "spring", stiffness: 100, damping: 10 },
                  opacity: { duration: 0.3 }
                }}
                className="absolute inset-0 flex flex-col md:flex-row items-center justify-center gap-11 md:gap-20 cursor-grab active:cursor-grabbing px-6"
              >
                <img
                  src="/assets/sectionimage.png"
                  alt="Store Preview"
                  className="w-[280px] sm:w-[350px] md:w-[400px] pointer-events-none select-none object-contain"
                />
                
                <div className="flex items-center md:items-start gap-2 flex-col justify-center w-full md:max-w-[400px]">
                  <h3 className="font-bold text-xl md:text-2xl text-gray-900">{slides[slideIndex].title}</h3>
                  <ul className="md:list-disc list-none text-center sm:text-start flex flex-col items-center md:items-start justify-center gap-3">
                    {slides[slideIndex].items.map((item, index) => (
                      <li key={index} className="text-[#5C5C5C] leading-relaxed">
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>

          <div className="flex gap-3 items-center justify-center mt-12">
            {slides.map((_, index) => (
              <button
                key={index}
                onClick={() => setSlideIndex(index)}
                className={`h-2.5 rounded-full transition-all duration-500 ease-in-out cursor-pointer ${
                  slideIndex === index ? "w-8 bg-[#7E57C2]" : "w-2.5 bg-[#CBD5E1] hover:bg-[#B0BCCB]"
                }`}
              />
            ))}
          </div>
        </div>
        </motion.div>
        <div className="bg-[#4C1D95] py-16 px-4 flex flex-col items-center justify-center gap-5 landing-pricing bg-no-repeat bg-left">
          <div className="flex flex-col items-center justify-center">
            <h5 className="text-[#A78BFA] font-bold tracking-[3px]">PRICING</h5>
            <h2 className="font-bold text-white text-center">
              Simple Pricing for your business
            </h2>
          </div>
          <p className="text-white">
            All features for just {prices.symbol}{prices.starter_monthly.toLocaleString()}
          </p>
          {hasValidSession ? (
            <a
              href={config.DASHBOARD_URL}
              onClick={handleDashboardClick}
              className="w-full flex justify-center"
            >
              <Button
                text="Go to dashboard"
                className=" w-full rounded-lg primary-btn md:w-fit cursor-pointer text-white font-bold shadow-lg"
              ></Button>
            </a>
          ) : (
            <Link to="signup" className="w-full flex justify-center">
              <Button
                text=" Start for Free"
                className=" w-full rounded-lg primary-btn md:w-fit cursor-pointer text-white font-bold shadow-lg"
              ></Button>
            </Link>
          )}
        </div>
        <div className="px-4 md:px-8">
          <div className="py-10" id="faq">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "0px 0px -100px 0px" }}
              animate="visible"
              className="flex items-center justify-center gap-8 flex-col"
            >
              <div className="flex items-center justify-center text-center flex-col">
                <motion.h2
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, margin: "0px 0px -100px 0px" }}
                  variants={contentVariants}
                  className="font-bold"
                >
                  Frequently Asked Questions
                </motion.h2>
                <motion.span
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, margin: "0px 0px -100px 0px" }}
                  variants={contentVariants}
                  className="text-[#333333] leading-[160%]"
                >
                  Find questions and answers related to your problems
                </motion.span>
              </div>
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "0px 0px -100px 0px" }}
                variants={contentVariants}
                className="flex items-center justify-center gap-4 flex-col w-full min-w-[280px] max-w-[770px]"
              >
                <FAQItem
                  question="Do I need technical skills to use JelloSite?"
                  answer={
                    <>
                      No you do not need any technical skills, it's easy to
                      navigate and self explanatory.
                    </>
                  }
                />
                <FAQItem
                  question="How long does it take to set up my store?"
                  answer="Typically takes less than 5 minutes to get started with basic setup."
                />
                <FAQItem
                  question="What payment methods do you support?"
                  answer="We support all major payment gateways including Paystack and Flutterwave."
                />
                <FAQItem
                  question="Can I use my own domain name?"
                  answer="Yes, you can easily connect your custom domain in your dashboard settings."
                />
                <FAQItem
                  question="Is there a mobile app?"
                  answer="Our platform is fully responsive and works great on any mobile browser."
                />
                <FAQItem
                  question="Can I sell digital products?"
                  answer="Yes! Our e-commerce platform supports the sales of digital products"
                />
              </motion.div>
            </motion.div>
          </div>
          <div>
            {/* <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "0px 0px -100px 0px" }}
              variants={contentVariants}
              className="mb-15"
            >
              <motion.h2
                variants={contentVariants}
                className="font-medium flex items-center gap-1 justify-center py-15"
              >
                Stop Chatting.<h2 className="text-[#945BF5]">Start Selling</h2>
              </motion.h2>
              <motion.div className="flex items-center md:items-start justify-center flex-col md:flex-row gap-20">
                <div className="flex items-start justify-center gap-3">
                  <motion.ul
                    initial="hidden"
                    animate="visible"
                    variants={contentVariants}
                    className="flex flex-col gap-3 items-center justify-center list-disc pl-4 pr-2"
                  >
                    <motion.li
                      initial="hidden"
                      animate="visible"
                      variants={contentVariants}
                      className="py-2 text-[#333333CC] border-b border-b-[#E4E4E4] w-full"
                    >
                      Every day your business operates only on WhatsApp or
                      Instagram is another day of missed opportunities.
                    </motion.li>
                    <motion.li
                      initial="hidden"
                      animate="visible"
                      variants={contentVariants}
                      className="py-2 text-[#333333CC] w-full"
                    >
                      While you're manually answering the same product
                      questions... While you're waiting for payment
                      screenshots... While your store is "closed" because you're
                      sleeping...
                    </motion.li>
                    <motion.li
                      initial="hidden"
                      animate="visible"
                      variants={contentVariants}
                      className="py-2 text-[#333333CC] border-t border-t-[#E4E4E4] w-full"
                    >
                      Your competitors with websites are making sales
                      automatically.
                    </motion.li>
                    <motion.li
                      initial="hidden"
                      animate="visible"
                      variants={contentVariants}
                      className="py-2 text-[#333333CC] border-t border-t-[#E4E4E4] w-full"
                    >
                      The difference between a struggling social media business
                      and a thriving brand isn't luck. It's having professional
                      tools that work for you 24/7.
                    </motion.li>
                    <motion.li
                      initial="hidden"
                      animate="visible"
                      variants={contentVariants}
                      className="py-2 text-[#333333CC] border-t border-t-[#E4E4E4] w-full"
                    >
                      For less than the cost of your data plan, you can
                      transform your business today.
                    </motion.li>
                  </motion.ul>
                </div>
                <img
                  src="/assets/stopchatting.png"
                  className="sm:w-[450px] w-full"
                  alt=""
                />
              </motion.div>
            </motion.div> */}

            <div className="flex items-center text-center justify-center flex-col gap-4 mt-15 md:mt-20">
              <div className="flex items-center gap-4 flex-col md:flex-row">
                <div className="flex -space-x-3">
                    <img
                      className="w-8 h-8 rounded-full border-2 border-white object-cover"
                      src="/assets/entp1.png"
                      alt="User 1"
                    />
                    <img
                      className="w-8 h-8 rounded-full border-2 border-white object-cover"
                      src="/assets/entp2.png"
                      alt="User 2"
                    />
                    <img
                      className="w-8 h-8 rounded-full border-2 border-white object-cover"
                      src="/assets/entp3.png"
                      alt="User 3"
                    />
                  </div>
              <h3 className=" font-bold italic">
                Join 500+ entrepreneurs already growing with Jellosite
              </h3>
             </div>
              <h4 className=" text-[#333333CC]">
                Five minutes to set up. Zero technical skills needed.
              </h4>
              {hasValidSession ? (
                <a href={config.DASHBOARD_URL} onClick={handleDashboardClick}>
                  <Button
                    text="Go to dashboard"
                    className="w-full px-6 font-bold sm:w-fit bg-[#945bf5] rounded-[40px] transition-all duration-300 ease-in-out hover:bg-white hover:text-[#945bf5]  hover:shadow-[0_0_0_2px_#945bf5]"
                  />
                </a>
              ) : (
                <Link to="signup">
                  <Button
                    text="Start for free"
                    className="w-full px-6 font-bold sm:w-fit bg-[#945bf5] rounded-[40px] transition-all duration-300 ease-in-out hover:bg-white hover:text-[#945bf5]  hover:shadow-[0_0_0_2px_#945bf5]"
                  />
                </Link>
              )}
            </div>
          </div>
        </div>
      </motion.div>
      <Footer />
    </motion.div>
  );
};

export default LandingPage;

const FAQItem = ({
  question,
  answer,
}: {
  question: string;
  answer: React.ReactNode;
}) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div
      className="flex items-center justify-between bg-[#33333308] p-4 rounded-2xl w-full"
      onClick={() => setIsOpen(!isOpen)}
    >
      <div className="flex flex-col gap-1 w-full justify-center items-start">
        <h5 className="font-medium text-[#333333CC]">{question}</h5>
        <div
          className={`overflow-hidden transition-all duration-300 ease-in-out ${
            isOpen ? "max-h-[1000px] opacity-100" : "max-h-0 opacity-0"
          }`}
        >
          <p className="text-[#33333380]">{answer}</p>
        </div>
      </div>
      <div className="text-[#333333CC] hover:text-[#7E57C2] transition-colors cursor-pointer">
        {isOpen ? <Minus size={14} /> : <Plus size={14} />}
      </div>
    </div>
  );
};
